import React, { useState, useMemo } from "react";
import { Box } from "@mui/material";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import { useConsultation } from "../../ConsultationContext";
// Use the same silhouette + organ overlay that BodyMapTab uses, so the body
// figure renders byte-identical between the regular system tabs and the
// Body Composition tab — no positional drift between baked-in vs. overlaid
// organs (which is what we got when we used `bodyImgFull.png`).
import bodyImg from "../../../platform/images/bodyImg.png";
import HumanFigureOrgans from "../../../platform/components/score/HumanFigureOrgans";
import ScrollableWrapper from "../../../platform/components/common/ScrollableWrapper";
import PillTabs from "./PillTabs";
import {
  BodyCompositionData,
  BodyCompositionSegmentId,
  BodyCompositionTab,
} from "../../data/consultationTypes";

/**
 * Body Composition right-column view (Section 2 sub-step `body-composition`).
 *
 * Mirrors Figma node 14725:50370:
 *   - Pill-segmented tab picker (Lean / Fat / ECW/TBW / ECW / ICW / TBW)
 *   - Body figure (silhouette + HumanFigureOrgans overlay — same as BodyMapTab)
 *   - 5 floating segment cards positioned around the body
 *     (Left/Right Arm, Trunk, Left/Right Leg)
 *   - Device label ("Inbody 970S") pinned to the bottom of the panel
 *
 * Organ-click side effects from `DataPointOrgan` inside `HumanFigureOrgans`
 * are suppressed via `onClickCapture` on the figure container — clicks still
 * work in the score screen where the same component is mounted normally.
 * The tab picker sits OUTSIDE the click-suppressed area so it stays interactive.
 */

const SEGMENT_LABELS: Record<BodyCompositionSegmentId, string> = {
  leftArm: "Left Arm",
  rightArm: "Right Arm",
  trunk: "Trunk",
  leftLeg: "Left Leg",
  rightLeg: "Right Leg",
};

const FIGURE_WIDTH = 490;

// Card positions inside the 490px-wide figure container (matches
// humanFigureContainer's width). Anchored as % so they stay aligned with
// body parts at any image height.
const SEGMENT_POSITIONS: Record<
  BodyCompositionSegmentId,
  { top: string; left?: string; right?: string }
> = {
  leftArm: { top: "20%", left: "0" },
  rightArm: { top: "20%", right: "0" },
  trunk: { top: "40%", left: "50%" },
  leftLeg: { top: "65%", left: "0" },
  rightLeg: { top: "65%", right: "0" },
};

/**
 * Pure presentational body-composition figure. Takes the body comp data via
 * props (no context coupling) so it can be reused on the score screen, which
 * doesn't have a `ConsultationProvider` available. The default
 * `BodyCompositionView` export below is a thin wrapper that reads the
 * consultation context and delegates here.
 */
export const BodyCompositionFigure: React.FC<{
  bodyComposition: BodyCompositionData | null | undefined;
}> = ({ bodyComposition }) => {
  const theme: Theme = useTheme();
  const tabs: BodyCompositionTab[] = bodyComposition?.tabs ?? [];
  const deviceName: string = bodyComposition?.deviceName ?? "";

  const [activeLabel, setActiveLabel] = useState<string>(
    tabs[0]?.label ?? ""
  );

  const activeTab = useMemo(
    () => tabs.find((t) => t.label === activeLabel) ?? tabs[0],
    [tabs, activeLabel]
  );

  if (!activeTab) return null;

  const segmentIds: BodyCompositionSegmentId[] = [
    "leftArm",
    "rightArm",
    "trunk",
    "leftLeg",
    "rightLeg",
  ];

  return (
    <Box sx={styles.wrap}>
      <Box sx={styles.tabsRow}>
        <PillTabs
          options={tabs.map((t) => t.label)}
          active={activeLabel}
          onSelect={setActiveLabel}
          fullWidth
        />
      </Box>

      <Box sx={styles.scrollArea}>
        <ScrollableWrapper>
          <Box sx={styles.figureOuter}>
            {/* Capture organ-dot clicks (DataPointOrgan inside HumanFigureOrgans)
                so the consultation doesn't trigger the score-screen Redux
                dispatches. The tab picker is outside this wrapper. */}
            <Box
              sx={styles.figureContainer}
              onClickCapture={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
            >
              <Box sx={styles.figureWrapper}>
                <Box
                  component="img"
                  src={bodyImg}
                  alt="Body composition figure"
                  sx={styles.figureImage}
                />
                {/* Greyed organs (40% opacity) — silhouette stays full opacity. */}
                <Box sx={styles.organsLayer}>
                  <HumanFigureOrgans showDataPoints={false} />
                </Box>
              </Box>

              {segmentIds.map((id) => {
                const seg = activeTab.segments[id];
                // Skip cards for segments the backend didn't provide. The
                // brief expects all 5 (`leftArm | rightArm | trunk | leftLeg
                // | rightLeg`), but until backend ships keys in that shape
                // (discrepancy #5), normalizers in fetchConsultationPrep may
                // not be able to resolve every key.
                if (!seg) return null;
                // Some tabs (e.g. TBW, ICW, ECW) ship `percentage: null` —
                // those metrics don't have a "% of reference" comparison.
                // Treat empty strings the same way so we don't render an
                // awkward empty styled row.
                const hasValue =
                  seg.value != null && String(seg.value).trim() !== "";
                const hasPercentage =
                  seg.percentage != null &&
                  String(seg.percentage).trim() !== "";
                if (!hasValue && !hasPercentage) return null;
                const pos = SEGMENT_POSITIONS[id];
                const transform =
                  id === "trunk" ? "translateX(-50%)" : undefined;
                return (
                  <Box
                    key={id}
                    sx={{
                      ...styles.card(theme),
                      top: pos.top,
                      left: pos.left,
                      right: pos.right,
                      transform,
                    }}
                  >
                    <Box sx={styles.cardLabel(theme)}>{SEGMENT_LABELS[id]}</Box>
                    {hasValue && (
                      <Box sx={styles.cardValue(theme)}>{seg.value}</Box>
                    )}
                    {hasPercentage && (
                      <Box sx={styles.cardValue(theme)}>{seg.percentage}</Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </ScrollableWrapper>
      </Box>

      {deviceName && (
        <Box sx={styles.deviceLabel(theme)}>{deviceName}</Box>
      )}
    </Box>
  );
};

/**
 * Consultation-bound wrapper — reads `bodyComposition` from the consultation
 * context and passes it to `BodyCompositionFigure`. Exists so consultation
 * callers keep their existing `<BodyCompositionView />` usage unchanged after
 * the split; surfaces outside the consultation (e.g. the score screen) should
 * import `BodyCompositionFigure` directly and supply their own data.
 */
const BodyCompositionView: React.FC = () => {
  const { data } = useConsultation();
  return <BodyCompositionFigure bodyComposition={data.prep?.bodyComposition} />;
};

export default BodyCompositionView;

const styles = {
  wrap: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column" as const,
    gap: 0,
    padding: "16px 16px 0",
    position: "relative" as const,
  },
  tabsRow: {
    display: "flex",
    width: "100%",
  },
  scrollArea: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column" as const,
  },
  figureOuter: {
    minHeight: "100%",
    display: "flex",
    flexDirection: "column" as const,
    justifyContent: "flex-end",
  },
  // Same dimensions and layout chain as BodyMapTab's humanFigureContainer
  // → humanFigureWrapper, so HumanFigureOrgans' absolute positions land in
  // the same pixel coordinates as in the score screen.
  figureContainer: {
    position: "relative" as const,
    width: `${FIGURE_WIDTH}px`,
    margin: "0 auto",
    flexShrink: 0,
  },
  figureWrapper: {
    position: "relative" as const,
    width: "100%",
    display: "flex",
    alignItems: "flex-end",
  },
  figureImage: {
    display: "block",
    width: "100%",
    height: "auto",
    pointerEvents: "none" as const,
  },
  // Wrapper around HumanFigureOrgans — applies the 40% opacity ONLY to the
  // organ overlays so the body silhouette stays full-color underneath.
  organsLayer: {
    position: "absolute" as const,
    inset: 0,
    opacity: 0.4,
    pointerEvents: "none" as const,
  },
  card: (theme: Theme) => ({
    position: "absolute" as const,
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    boxShadow:
      "0px 8px 20px rgba(0,0,0,0.06), 0px 2px 8px rgba(0,0,0,0.04)",
    padding: "8px",
    display: "flex",
    flexDirection: "column" as const,
    gap: "8px",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "94px",
    zIndex: 2,
  }),
  cardLabel: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "normal",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  cardValue: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "15px",
    letterSpacing: "-0.13px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    padding: "4px",
  }),
  deviceLabel: (theme: Theme) => ({
    position: "absolute" as const,
    bottom: "8px",
    right: "16px",
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "1.6",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    pointerEvents: "none" as const,
    zIndex: 3,
  }),
};
